'use client'

import { Widget } from '@typeform/embed-react'
import Markdown from 'markdown-to-jsx'
import React, { Suspense } from 'react'

import { ButtonLink } from '@/legacy-src/components/ui/button'
import useIsAppWebView from '@/legacy-src/hooks/useIsAppWebView'
import notifyMobileApp, { NotifyAppMessages } from '@/legacy-src/utilities/notifyMobileApp'

import { SelfAssessmentWizard } from '@/src/domains/self-assessments/components/SelfAssessmentWizard'
import { StrapiFaq } from '@/src/domains/strapi/components/StrapiFaq'
import { TypeformStatus } from '@/src/domains/typeform/constants'

import { getTranslate } from '../../utilities/i18n'
import BackButton from '../button/BackButton'
import { textToAnchor } from '../counselling/ArticleAnchors'
import Icon from '../icons/Icon'
import { ComponentNamesRest } from '../university/componentNames'
import TestYourself from './TestYourself'

const selectComponent = (componentName, item, key) => {
  return (
    {
      [ComponentNamesRest.richContent]: item.content && (
        <Markdown key={key} options={{ slugify: (str) => textToAnchor(str) }}>
          {item.content}
        </Markdown>
      ),

      [ComponentNamesRest.buttonLink]: (
        <div className="my-12 flex justify-center">
          <ButtonLink variant={item.style === 'link' ? 'inline' : 'default'} href={item.link} className="normal-case">
            {item.text}
            {item.withArrow && <Icon className="ml-6 text-white" icon="arrow" size="sm" />}
          </ButtonLink>
        </div>
      ),
    }[componentName] ?? null
  )
}

const MAX_WIDTH = 900

type Props = {
  readonly data: any
  readonly testsLink: string
}

// this component needs to be wrapped in suspense because of useSearchParams
const BackBtn: React.FC<{ testsLink: string }> = ({ testsLink }) => {
  const isWebview = useIsAppWebView()
  const { t } = getTranslate()

  const backButtonProps = isWebview
    ? { onClick: () => notifyMobileApp(NotifyAppMessages.BACK_BUTTON_CLICK) }
    : { link: testsLink }

  return (
    <div className="mb-12">
      <BackButton icon="back" text={t(`component.button.back`)} size="sm" {...backButtonProps} />
    </div>
  )
}

const TestsPage: React.FC<Props> = ({ data, testsLink }) => {
  const testContent = data

  return (
    <div className="w-full">
      {!testContent ? (
        <>
          <p>There is no content for this version of language.</p>
        </>
      ) : (
        <>
          <div className="flex justify-center">
            <div className={`max-w-[${MAX_WIDTH}px]`}>
              <div className="px-4 pb-8 big-tablet:px-16 big-tablet:pb-12">
                <Suspense>
                  <BackBtn testsLink={testsLink} />
                </Suspense>
                <h1 className="mb-4 font-semibold text-secondary">{testContent.title}</h1>
              </div>

              <div className="counselling__content px-4 big-tablet:px-16">
                {testContent.content && <Markdown>{testContent.content}</Markdown>}
                {testContent.description?.map((item, key) => {
                  return selectComponent(item.__component, item, `${item.__component}-${key}`)
                })}

                {testContent.selfAssessmentDefinition ? (
                  <div className="my-10" id="typeform" data-sentry-block>
                    <SelfAssessmentWizard definition={testContent.selfAssessmentDefinition} />
                  </div>
                ) : testContent.typeformStatus === TypeformStatus.AVAILABLE && testContent.typeFormId ? (
                  <div className="my-10" id="typeform">
                    <Widget id={testContent.typeFormId} style={{ height: 600 }} />
                  </div>
                ) : null}
                <Suspense>
                  <TestYourself categoryUrl={testsLink} />
                </Suspense>
              </div>
              {testContent.faq?.entry ? (
                <div className="px-4 pt-6 big-tablet:px-16">
                  <StrapiFaq items={testContent.faq.entry} />
                </div>
              ) : null}
              <div className="px-4 pb-8 big-tablet:px-16 big-tablet:pb-10">
                <hr className="hidden border-[2px] border-white laptop:block" />
                <div className="mb-13">
                  <Suspense>
                    <BackBtn testsLink={testsLink} />
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default TestsPage
